import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { searchAudit } from '../../api/admin/endpoints';
import type { AuditSortKey } from '../../api/admin/types';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDateTime } from '../../components/format';
import { PageHeader } from '../../components/PageHeader';
import { ServerPagination } from '../../components/ServerPagination';
import { SortableCell } from '../../components/SortableCell';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { useDebouncedValue } from '../../components/useDebouncedValue';
import { useSort } from '../../components/useSort';
import { useLanguage } from '../../i18n/LanguageContext';

/** Every super-admin action and every change to a tenant user (BR-20). `?tenantId=` narrows it to one business. */
export const AuditPage = () => {
  const { language, t } = useLanguage();
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get('tenantId') ?? undefined;
  const [action, setAction] = useState('');
  const [page, setPage] = useState(0);
  const sort = useSort<AuditSortKey>('createdAt', 'desc', () => setPage(0));
  const debouncedAction = useDebouncedValue(action);

  const query = useQuery({
    queryKey: ['audit', tenantId, debouncedAction, page, sort.key, sort.direction],
    queryFn: () =>
      searchAudit({
        tenantId,
        action: debouncedAction,
        page: page + 1,
        sort: sort.key,
        direction: sort.direction,
      }),
    placeholderData: keepPreviousData,
  });

  const rows = query.data?.items ?? [];

  return (
    <>
      <PageHeader title={t('audit.title')} subtitle={t('audit.subtitle')} />
      <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      <Paper variant="outlined">
        <TableFilters>
          <TextField
            size="small"
            label={t('audit.action')}
            placeholder="licence.suspended"
            value={action}
            onChange={(event) => {
              setAction(event.target.value);
              setPage(0);
            }}
          />
        </TableFilters>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="createdAt">
                  {t('audit.when')}
                </SortableCell>
                <TableCell>{t('audit.tenant')}</TableCell>
                <TableCell>{t('audit.actor')}</TableCell>
                <SortableCell sort={sort} column="action">
                  {t('audit.action')}
                </SortableCell>
                <TableCell>{t('audit.target')}</TableCell>
                <TableCell>{t('audit.note')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.isLoading ? (
                <TableSkeletonRows columns={6} />
              ) : (
                rows.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(entry.createdAt, language)}
                    </TableCell>
                    <TableCell>{entry.tenantName ?? t('common.none')}</TableCell>
                    <TableCell>
                      {entry.actorEmail ?? t('common.none')}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t(`actorKind.${entry.actorKind}` as const)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'ui-monospace, monospace' }}>{entry.action}</TableCell>
                    <TableCell>
                      {entry.targetType}
                      {entry.targetId ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {entry.targetId}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{entry.note ?? t('common.none')}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!query.isLoading && rows.length === 0 ? (
          <EmptyState title={t('audit.empty')} detail={t('audit.emptyDetail')} />
        ) : null}
        <ServerPagination
          page={page}
          pageSize={query.data?.pageSize ?? 25}
          total={query.data?.total ?? 0}
          onPageChange={setPage}
        />
      </Paper>
    </>
  );
};
